// Helpers: capture leads in the DB and either open WhatsApp or just send the
// request as an email-reply lead. The global WhatsAppLeadTracker only catches
// real <a href="wa.me/..."> clicks, so any programmatic flow must call these
// helpers to avoid losing the contact.
import { supabase } from "@/integrations/supabase/client";
import { handleWhatsAppClick } from "@/lib/analytics";

interface LogOpts {
  source: string;
  message: string; // already DECODED (human-readable) text
  language?: string;
  context?: Record<string, unknown>;
  pagePath?: string;
  /** Customer name/email/phone captured in the form (optional but recommended). */
  name?: string;
  email?: string;
  phone?: string;
  /** GDPR consent, required for channel=email. */
  consent?: boolean;
}

type Channel = "whatsapp" | "email";

export type LeadResult =
  | { ok: true }
  | { ok: false; error: "rate_limited" | "consent_required" | "email_required" | "network" };

async function logLead(channel: Channel, opts: LogOpts): Promise<LeadResult> {
  const { source, message, language, context, pagePath, name, email, phone, consent } = opts;
  try {
    const { data, error } = await supabase.functions.invoke("whatsapp-lead", {
      body: {
        channel,
        name: name || null,
        email: email || null,
        phone: phone || null,
        consent: consent === true,
        pagePath:
          pagePath ||
          (typeof window !== "undefined" ? window.location.pathname : null),
        source: source.slice(0, 100),
        message,
        language: language || "en",
        context,
      },
    });
    if (error) {
      // Edge function returned non-2xx, try to read the payload for the reason.
      const ctx = (error as { context?: Response }).context;
      try {
        const json = await ctx?.json();
        const reason = json?.error;
        if (reason === "rate_limited" || reason === "rate_limited_hour" || reason === "rate_limited_ip") {
          return { ok: false, error: "rate_limited" };
        }
        if (reason === "consent_required") return { ok: false, error: "consent_required" };
        if (reason === "email_required") return { ok: false, error: "email_required" };
      } catch { /* ignore */ }
      return { ok: false, error: "network" };
    }
    if (data && data.ok === false) {
      const reason = data.error;
      if (reason === "rate_limited" || reason === "rate_limited_hour" || reason === "rate_limited_ip") {
        return { ok: false, error: "rate_limited" };
      }
      if (reason === "consent_required") return { ok: false, error: "consent_required" };
      if (reason === "email_required") return { ok: false, error: "email_required" };
      return { ok: false, error: "network" };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "network" };
  }
}

export async function logAndOpenWhatsApp(whatsappNumber: string, opts: LogOpts) {
  // GA4 generate_lead + (optional) Google Ads conversion, fire BEFORE redirect
  // so the tag has time to send before the tab navigates away.
  try { handleWhatsAppClick(opts.source); } catch { /* ignore */ }
  await logLead("whatsapp", opts);
  if (typeof window !== "undefined") {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(opts.message)}`,
      "_blank"
    );
  }
}

/** Save the lead as an email-reply request. No redirect. Returns a structured result. */
export async function logEmailRequest(opts: LogOpts): Promise<LeadResult> {
  return logLead("email", opts);
}

